import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router";
import ReactTable, { RowInfo } from "react-table";
import * as ReactTooltip from "react-tooltip";
import { bindActionCreators } from "redux";
import { InlineLoading, Modal } from "src/components/common";
import { Link } from "src/components/routing";
import { FontAwesomeIcons } from "src/constants/fontAwesomeIcons";
import { PAGE_SIZE_OPTIONS } from "src/constants/reactTableConstants";
import { TrialInstitutionLaboratoriesBreadcrumbs } from "src/helpers/breadcrumbHelper";
import {
    convertToApproximateDateString,
    convertToShortDateString,
} from "src/helpers/date";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import * as TrialInstitutionActions from "../../../actions/trialInstitution";
import * as TrialInstitutionLaboratoryActions from "../../../actions/trialInstitutionLaboratory";
import { RestrictedLayout } from "../../../components/layouts/index";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { TrialInstitutionSubMenu } from "../../../helpers/subMenuHelper";
import { ITmdState } from "../../../reducers/index";

import { ModalSize } from "src/enumerations/ModalSize";
import "./TrialInstitutionLaboratoriesPage.scss";

interface TrialInstitutionLaboratoriesPageProps {
    trialInstitution: Dtos.TrialInstitution;
    loadingTrialInstitution: boolean;
    loadTrialInstitutionSuccess: boolean;
    loadTrialInstitutionFailure: boolean;
    loadTrialInstitutionErrorCode: string;
    trialInstitutionLaboratories: Dtos.TrialInstitutionLaboratory[];
    loadingTrialInstitutionLaboratories: boolean;
    loadTrialInstitutionLaboratoriesSuccess: boolean;
    loadTrialInstitutionLaboratoriesFailure: boolean;
    loadTrialInstitutionLaboratoriesErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    pageSize: number;
    deletingTrialInstitutionLaboratory: boolean;
    deleteTrialInstitutionLaboratorySuccess: boolean;
    clearTrialInstitution: TrialInstitutionActions.ITrialInstitutionClearActionCreator;
    loadTrialInstitution: TrialInstitutionActions.ITrialInstitutionLoadByIdActionCreator;
    clearTrialInstitutionLaboratories: TrialInstitutionLaboratoryActions.ITrialInstitutionLaboratoryClearActionCreator;
    loadTrialInstitutionLaboratories: TrialInstitutionLaboratoryActions.ITrialInstitutionLaboratoriesLoadByTrialInstitutionIdActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;
    deleteTrialInstitutionLaboratory: TrialInstitutionLaboratoryActions.ITrialInstitutionLaboratoryDeleteActionCreator;
}

const TrialInstitutionLaboratoriesPage: FC<
    TrialInstitutionLaboratoriesPageProps
> = ({
    trialInstitution,
    loadingTrialInstitution,
    loadTrialInstitutionSuccess,
    loadTrialInstitutionFailure,
    loadTrialInstitutionErrorCode,
    trialInstitutionLaboratories,
    loadingTrialInstitutionLaboratories,
    loadTrialInstitutionLaboratoriesSuccess,
    loadTrialInstitutionLaboratoriesFailure,
    loadTrialInstitutionLaboratoriesErrorCode,
    permissions,
    pageSize,
    deletingTrialInstitutionLaboratory,
    deleteTrialInstitutionLaboratorySuccess,
    clearTrialInstitution,
    loadTrialInstitution,
    clearTrialInstitutionLaboratories,
    loadTrialInstitutionLaboratories,
    setBreadcrumbs,
    setDrawerItems,
    setPageSize,
    deleteTrialInstitutionLaboratory
}) => {
    //
    // -- Params --
    const { trialId } = useParams<{ trialId: string }>();
    const { trialInstitutionId } = useParams<{ trialInstitutionId: string }>();
    const trialIdNumber = parseInt(trialId, 10);
    const trialInstitutionIdNumber = parseInt(trialInstitutionId, 10);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [showInActive, setShowInActive] = useState<boolean>(false);
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [labTrialInstitution, setLabTrialInstitution] = useState<
        Dtos.TrialInstitutionLaboratory | undefined
    >(undefined);

    //
    // -- Variables --
    const showTrialInstitutionLaboratories =
        !loadingTrialInstitution && 
        loadTrialInstitutionSuccess &&
        !loadingTrialInstitutionLaboratories &&
        loadTrialInstitutionLaboratoriesSuccess;


    // Load TrialInstitution & laboratories
    useEffect(() => {
        if (trialInstitutionIdNumber && !trialInstitution && !loadingTrialInstitution) {
            setBreadcrumbs(
                TrialInstitutionLaboratoriesBreadcrumbs(
                    trialIdNumber,
                    trialInstitutionIdNumber
                )
            );

            loadTrialInstitution(trialInstitutionIdNumber);
            loadTrialInstitutionLaboratories(trialInstitutionIdNumber);
        }
    }, [
        trialIdNumber,
        trialInstitutionIdNumber,
        trialInstitution,
        loadingTrialInstitution,
        setBreadcrumbs,
        loadTrialInstitution,
        loadTrialInstitutionLaboratories
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrialInstitutionFailure) {
            navigate(`/error/${loadTrialInstitutionErrorCode}`);
        }
        if (loadTrialInstitutionLaboratoriesFailure) {
            navigate(`/error/${loadTrialInstitutionLaboratoriesErrorCode}`);
        }
    }, [
        loadTrialInstitutionFailure, 
        loadTrialInstitutionErrorCode, 
        loadTrialInstitutionLaboratoriesFailure, 
        loadTrialInstitutionLaboratoriesErrorCode, 
        navigate
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (
            trialInstitutionIdNumber &&
            trialInstitution?.trial &&
            trialInstitution?.institution
        ) {
            setDrawerItems(
                TrialInstitutionSubMenu(trialInstitution, permissions)
            );
            setBreadcrumbs(
                TrialInstitutionLaboratoriesBreadcrumbs(
                    trialIdNumber,
                    trialInstitutionIdNumber,
                    trialInstitution.trial?.displayName,
                    trialInstitution.institution?.name
                )
            );
        }
    }, [
        trialIdNumber,
        trialInstitution,
        trialInstitutionIdNumber,
        permissions,
        setDrawerItems,
        setBreadcrumbs,
    ]);

    // Cleanup effect to clear the trial institution & laboratories on component unmount
    useEffect(() => {
        return () => {
            clearTrialInstitution();
            clearTrialInstitutionLaboratories();
        };
    }, [clearTrialInstitution, clearTrialInstitutionLaboratories]);

    // Reload trial institution & laboratories after delete
    useEffect(() => {
        if (
            !deletingTrialInstitutionLaboratory &&
            deleteTrialInstitutionLaboratorySuccess
        ) {
            loadTrialInstitution(trialInstitutionIdNumber);
            loadTrialInstitutionLaboratories(trialInstitutionIdNumber);
        }
    }, [
        deletingTrialInstitutionLaboratory,
        deleteTrialInstitutionLaboratorySuccess,
        loadTrialInstitution,
        trialInstitutionIdNumber,
        loadTrialInstitutionLaboratories
    ]);

    const generateDeleteButton = (
        trialInstitutionLab: Dtos.TrialInstitutionLaboratory
    ) => {
        const hasPermission = AuthenticationHelper.hasPermission(
            permissions,
            Dtos.Permission.TrialInstitutionLabUpdate,
            undefined,
            undefined
        );

        if (!hasPermission) return null;

        return (
            <button
                key={`labDeleteButton-${trialInstitutionLab.id}`}
                className="btn btn-outline-secondary btn-sm btn-row-version"
                onClick={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    setLabTrialInstitution(trialInstitutionLab);
                    setOpenModal(true);
                }}
            >
                <FontAwesomeIcon
                    icon={FontAwesomeIcons.Duotone.TRASH}
                    fixedWidth
                />
            </button>
        );
    };

    const createTable = useMemo(() => {
        if (
            !showInActive &&
            trialInstitutionLaboratories?.filter((x) => x?.active == true)
                ?.length === 0 &&
            trialInstitutionLaboratories?.filter((x) => x?.active == false)
                ?.length > 0
        ) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>
                            There are currently no active trial institution
                            laboratories to view.
                        </span>
                    </div>
                </div>
            );
        }

        let tableData = showInActive
            ? trialInstitutionLaboratories
            : trialInstitutionLaboratories?.filter((x) => x?.active == true);

        if (!tableData || tableData?.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>
                            There are currently no trial institution
                            laboratories to view.
                        </span>
                    </div>
                </div>
            );
        }

        const showPagination = tableData.length > pageSize;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={tableData}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination ? pageSize : tableData.length
                    }
                    pageSize={showPagination ? pageSize : tableData.length}
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no trial institution laboratories to display"
                    resizable={false}
                    onPageSizeChange={(val) => setPageSize(val)}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (_e, handleOriginal) => {
                                const url: string = `/trials/${trialIdNumber}/trialinstitution/${trialInstitutionIdNumber}/labs/${rowInfo.original.id}/details`;
                                navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    defaultSorted={[
                        {
                            id: "name",
                            desc: false,
                        },
                    ]}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Duotone.INSTITUTION}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "laboratory.name",
                            Header: "Laboratory",
                            accessor: "laboratory.name",
                        },
                        {
                            id: "dateCeased",
                            Header: "Date Ceased",
                            accessor: "dateCeased",
                            Cell: (props) => (
                                <div>
                                    <span
                                        data-tip={convertToApproximateDateString(
                                            props.value
                                        )}
                                    >
                                        {convertToShortDateString(props.value)}
                                    </span>
                                    <ReactTooltip />
                                </div>
                            ),
                        },
                        {
                            id: "deleteDepartment",
                            Header: " ",
                            maxWidth: 75,
                            Cell: (props) => (
                                <div>
                                    {generateDeleteButton(props.original)}
                                </div>
                            ),
                        },
                    ]}
                />
            </div>
        );
    }, [
        trialInstitutionLaboratories,
        pageSize,
        PAGE_SIZE_OPTIONS,
        navigate,
        setPageSize,
        showInActive,
        trialIdNumber,
    ]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-6 mb-2"}>
                        <h1>Laboratories</h1>
                    </div>
                    <div className="col-6" style={{ textAlign: "right" }}>
                        <button
                            onClick={() => {
                                setShowInActive(!showInActive);
                            }}
                            type="button"
                            className="btn btn-outline-dark mr-2"
                            style={{ minWidth: "125px" }}
                        >
                            {showInActive ? "Hide Inactive" : "Show Inactive"}
                        </button>
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.TrialInstitutionLabCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={`/trials/${trialIdNumber}/trialInstitution/${trialInstitutionIdNumber}/labs/create`}
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showTrialInstitutionLaboratories ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showTrialInstitutionLaboratories ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [
        AuthenticationHelper,
        permissions,
        showTrialInstitutionLaboratories,
        createTable,
        setShowInActive,
        showInActive,
        trialIdNumber,
    ]);

    const renderDeleteConfirmation = useMemo(() => {
        const hasPermission = AuthenticationHelper.hasPermission(
            permissions,
            Dtos.Permission.TrialInstitutionLabUpdate,
            undefined,
            undefined
        );

        if (hasPermission) {
            if (
                !labTrialInstitution?.personnels ||
                labTrialInstitution?.personnels?.length == 0
            ) {
                return (
                    <Modal
                        open={openModal}
                        disableCloseOnOverlayClick={true}
                        onClose={() => {
                            setOpenModal(false);
                            setLabTrialInstitution(undefined);
                        }}
                        size={ModalSize.Md}
                    >
                        <div className="modal-header">
                            <div
                                className="modal-title"
                                style={{ minWidth: "540px" }}
                            >
                                <h2 className="mb-0">Delete</h2>
                            </div>
                            <button
                                type="button"
                                className="close"
                                aria-label="Close"
                                onClick={() => {
                                    setOpenModal(false);
                                    setLabTrialInstitution(undefined);
                                }}
                            >
                                <FontAwesomeIcon
                                    fixedWidth
                                    icon={FontAwesomeIcons.Regular.TIMES}
                                />
                            </button>
                        </div>
                        <div className="modal-body">
                            <span>
                                Are you sure you want to delete the laboratory "
                                <em>
                                    {labTrialInstitution?.laboratory?.name}"
                                </em>{" "}
                                from institution{" "}
                                <strong>
                                    {trialInstitution?.trial?.displayName} -{" "}
                                    {trialInstitution?.institution?.name}
                                </strong>
                                .
                            </span>
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => {
                                    deleteTrialInstitutionLaboratory(
                                        labTrialInstitution?.id
                                    );
                                    setOpenModal(false);
                                    setLabTrialInstitution(undefined);
                                }}
                            >
                                Delete
                            </button>
                            <button
                                type="button"
                                className="btn btn-outline-primary"
                                onClick={() => {
                                    setOpenModal(false);
                                    setLabTrialInstitution(undefined);
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </Modal>
                );
            } else {
                return (
                    <Modal
                        open={openModal}
                        disableCloseOnOverlayClick={true}
                        onClose={() => {
                            setOpenModal(false);
                            setLabTrialInstitution(undefined);
                        }}
                        size={ModalSize.Md}
                    >
                        <div className="modal-header">
                            <div
                                className="modal-title"
                                style={{ minWidth: "540px" }}
                            >
                                <h2 className="mb-0">Restricted</h2>
                            </div>
                            <button
                                type="button"
                                className="close"
                                aria-label="Close"
                                onClick={() => {
                                    setOpenModal(false);
                                    setLabTrialInstitution(undefined);
                                }}
                            >
                                <FontAwesomeIcon
                                    fixedWidth
                                    icon={FontAwesomeIcons.Regular.TIMES}
                                />
                            </button>
                        </div>
                        <div className="modal-body">
                            <span>
                                Laboratory "
                                <em>
                                    {labTrialInstitution?.laboratory?.name}"
                                </em>{" "}
                                can't be delete from trial institution{" "}
                                <strong>
                                    {trialInstitution?.trial?.displayName} -{" "}
                                    {trialInstitution?.institution?.name}
                                </strong>{" "}
                                because it contains personnel.
                            </span>
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => {
                                    setOpenModal(false);
                                    setLabTrialInstitution(undefined);
                                }}
                            >
                                Okay
                            </button>
                        </div>
                    </Modal>
                );
            }
        }
        return null;
    }, [
        labTrialInstitution,
        openModal,
        permissions,
        setOpenModal,
        labTrialInstitution,
        setLabTrialInstitution,
        trialInstitutionIdNumber,
        loadTrialInstitution,
    ]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.TrialInstitutionLabView}
            loading={loadingTrialInstitution || loadingTrialInstitutionLaboratories}
        >
            {renderContent}
            {renderDeleteConfirmation}
        </RestrictedLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<TrialInstitutionLaboratoriesPageProps> => {
    let trialInstitution: Dtos.TrialInstitution | undefined = !(
        state.trialInstitution.formData instanceof Array
    )
        ? state.trialInstitution.formData
        : undefined;
    let trialInstitutionLaboratories: Dtos.TrialInstitutionLaboratory[] | undefined = (state.trialInstitutionLaboratory.formData instanceof Array)
        ? state.trialInstitutionLaboratory.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        trialInstitution: trialInstitution!,
        loadingTrialInstitution:
            state.trialInstitution.loadState &&
            state.trialInstitution.loadState.status == RequestState.Pending,
        loadTrialInstitutionSuccess:
            state.trialInstitution.loadState &&
            state.trialInstitution.loadState.status == RequestState.Success,
        loadTrialInstitutionFailure:
            state.trialInstitution.loadState &&
            state.trialInstitution.loadState.status == RequestState.Failure,
        loadTrialInstitutionErrorCode:
            state.trialInstitution.loadState.errorCode,
        trialInstitutionLaboratories: trialInstitutionLaboratories!,
        loadingTrialInstitutionLaboratories:
            state.trialInstitutionLaboratory.loadState &&
            state.trialInstitutionLaboratory.loadState.status == RequestState.Pending,
        loadTrialInstitutionLaboratoriesSuccess:
            state.trialInstitutionLaboratory.loadState &&
            state.trialInstitutionLaboratory.loadState.status == RequestState.Success,
        loadTrialInstitutionLaboratoriesFailure:
            state.trialInstitutionLaboratory.loadState &&
            state.trialInstitutionLaboratory.loadState.status == RequestState.Failure,
        loadTrialInstitutionLaboratoriesErrorCode:
            state.trialInstitutionLaboratory.loadState.errorCode,
        permissions: permissions,
        pageSize: state.pagingPage.pageSize,
        deletingTrialInstitutionLaboratory:
            state.trialInstitutionLaboratory.deleteState &&
            state.trialInstitutionLaboratory.deleteState.status ==
                RequestState.Pending,
        deleteTrialInstitutionLaboratorySuccess:
            state.trialInstitutionLaboratory.deleteState &&
            state.trialInstitutionLaboratory.deleteState.status ==
                RequestState.Success,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<TrialInstitutionLaboratoriesPageProps> => {
    return {
        loadTrialInstitution: bindActionCreators(
            TrialInstitutionActions.LoadTrialInstitutionById,
            dispatch
        ),
        clearTrialInstitution: bindActionCreators(
            TrialInstitutionActions.Clear,
            dispatch
        ),
        loadTrialInstitutionLaboratories: bindActionCreators(
            TrialInstitutionLaboratoryActions.LoadTrialInstitutionLaboratoriesByTrialInstitutionId,
            dispatch
        ),
        clearTrialInstitutionLaboratories: bindActionCreators(
            TrialInstitutionLaboratoryActions.Clear,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        ),
        setPageSize: bindActionCreators(
            PagingActions.PageSizeChanged,
            dispatch
        ),
        deleteTrialInstitutionLaboratory: bindActionCreators(
            TrialInstitutionLaboratoryActions.DeleteTrialInstitutionLaboratory,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(TrialInstitutionLaboratoriesPage);
